import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff } from "lucide-react";

import "./AlbumGallerySlider.css";

export default function AlbumGallerySlider({
  images = [],
  activeIndex = 0,
  onIndexChange,
  className = "",
}) {
  const touchStart = useRef(null);
  const [loadedUrl, setLoadedUrl] = useState(null);
  const [failedUrl, setFailedUrl] = useState(null);

  const photos = Array.isArray(images) ? images : [];
  const total = photos.length;

  const index = Math.min(
    Math.max(Number(activeIndex) || 0, 0),
    Math.max(total - 1, 0),
  );

  const currentImage = photos[index];

  const imageUrl =
    currentImage?.imageUrl ||
    currentImage?.signedUrl ||
    currentImage?.thumbnailUrl ||
    null;

  const isLoaded = Boolean(imageUrl && loadedUrl === imageUrl);
  const hasError = Boolean(imageUrl && failedUrl === imageUrl);
  const canNavigate = total > 1 && typeof onIndexChange === "function";

  const goTo = (nextIndex) => {
    if (!canNavigate) return;

    onIndexChange((nextIndex + total) % total);
  };

  useEffect(() => {
    if (!canNavigate) return;

    const handleKeyDown = (event) => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(event.target?.tagName)
      ) {
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        onIndexChange((index + 1) % total);
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onIndexChange((index - 1 + total) % total);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canNavigate, index, total, onIndexChange]);

  const handleTouchStart = (event) => {
    if (!canNavigate || event.touches.length !== 1) return;

    touchStart.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  };

  const handleTouchEnd = (event) => {
    if (!touchStart.current || !canNavigate) return;

    const deltaX = event.changedTouches[0].clientX - touchStart.current.x;

    const deltaY = event.changedTouches[0].clientY - touchStart.current.y;

    touchStart.current = null;

    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    goTo(index + (deltaX < 0 ? 1 : -1));
  };

  if (!currentImage) {
    return (
      <div className="album-gallery-slider__empty">
        <ImageOff size={30} strokeWidth={1.8} />
        <p>No photos available.</p>
      </div>
    );
  }

  return (
    <div
      className={["album-gallery-slider", className].filter(Boolean).join(" ")}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        touchStart.current = null;
      }}
      aria-label="Album photo gallery"
    >
      <div className="album-gallery-slider__media">
        {imageUrl && !isLoaded && !hasError && (
          <div className="album-gallery-slider__loading" aria-live="polite">
            Loading photo...
          </div>
        )}

        {(!imageUrl || hasError) && (
          <div className="album-gallery-slider__empty">
            <ImageOff size={32} strokeWidth={1.8} />
            <p>Photo unavailable.</p>
          </div>
        )}

        {imageUrl && !hasError && (
          <img
            key={imageUrl}
            className={[
              "album-gallery-slider__image",
              isLoaded && "album-gallery-slider__image--loaded",
            ]
              .filter(Boolean)
              .join(" ")}
            src={imageUrl}
            alt={
              currentImage?.title ||
              currentImage?.description ||
              `Photo ${index + 1} of ${total}`
            }
            draggable={false}
            decoding="async"
            onLoad={() => setLoadedUrl(imageUrl)}
            onError={() => setFailedUrl(imageUrl)}
          />
        )}
      </div>

      {canNavigate && (
        <>
          <button
            type="button"
            className="album-gallery-slider__arrow album-gallery-slider__arrow--previous"
            onClick={() => goTo(index - 1)}
            aria-label="Previous photo"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            type="button"
            className="album-gallery-slider__arrow album-gallery-slider__arrow--next"
            onClick={() => goTo(index + 1)}
            aria-label="Next photo"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      <div className="album-gallery-slider__counter" aria-live="off">
        {index + 1} / {total}
      </div>
    </div>
  );
}
